import { GROUP_MESSAGE_TO_USER_ID, Message } from '../types/typesMessages';

const OPEN_MESSAGES_EVENT = 'repairlab:open-messages';
const NOTIFIED_IDS_MAX = 200;
const SOUND_COOLDOWN_MS = 1200;
const publicBase = String(process.env.PUBLIC_URL || '').replace(/\/$/, '');
const MESSAGE_WHISTLE_SRC = `${publicBase}/sounds/message-whistle.wav`;

let messagesDialogOpen = false;
let activeChatContactId: string | null = null;
let pendingOpenContactId: string | null = null;
let audioUnlocked = false;
let lastSoundAt = 0;
let whistleAudio: HTMLAudioElement | null = null;
const notifiedMessageIds = new Set<string>();

function getWhistleAudio(): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null;
  if (!whistleAudio) {
    whistleAudio = new Audio(MESSAGE_WHISTLE_SRC);
    whistleAudio.preload = 'auto';
    whistleAudio.volume = 0.88;
    whistleAudio.addEventListener(
      'error',
      () => {
        // eslint-disable-next-line no-console
        console.warn('[Messages] whistle file failed to load:', MESSAGE_WHISTLE_SRC);
        whistleAudio = null;
      },
      { once: true },
    );
  }
  return whistleAudio;
}

function trimPreview(text: string, max = 120): string {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return 'הודעה חדשה';
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

function shouldSuppressForOpenChat(fromUserId: string, conversationId?: string): boolean {
  const groupKey = conversationId ? `group:${conversationId}` : '';
  const active = activeChatContactId || '';
  const matchesDirect = active === fromUserId;
  const matchesGroup = Boolean(groupKey && active === groupKey);
  return messagesDialogOpen && (matchesDirect || matchesGroup) && document.hasFocus();
}

function unlockAudioOnce(): void {
  if (audioUnlocked || typeof window === 'undefined') return;
  const audio = getWhistleAudio();
  if (!audio) return;
  audio.muted = true;
  void audio
    .play()
    .then(() => {
      audio.pause();
      audio.currentTime = 0;
      audio.muted = false;
      audioUnlocked = true;
    })
    .catch(() => {
      audio.muted = false;
    });
}

/** שריקה — הקובץ שהועלה: mixkit-cartoon-whistling-738.wav */
function playNotificationSound(): void {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  if (now - lastSoundAt < SOUND_COOLDOWN_MS) return;
  lastSoundAt = now;
  const audio = getWhistleAudio();
  if (!audio) return;
  try {
    audio.currentTime = 0;
    void audio.play().catch(() => {
      // דפדפן חוסם אוטופליי עד אינטראקציה — unlockAudioOnce מטפל בזה
    });
  } catch {
    // ignore
  }
}

function vibrateMobile(): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate([100, 50, 100]);
    }
  } catch {
    // ignore
  }
}

function rememberNotified(messageId: string): boolean {
  const id = String(messageId || '').trim();
  if (!id) return false;
  if (notifiedMessageIds.has(id)) return false;
  notifiedMessageIds.add(id);
  if (notifiedMessageIds.size > NOTIFIED_IDS_MAX) {
    const first = notifiedMessageIds.values().next().value;
    if (first) notifiedMessageIds.delete(first);
  }
  return true;
}

export async function requestMessageNotificationPermission(): Promise<NotificationPermission | 'unsupported'> {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  if (Notification.permission === 'granted' || Notification.permission === 'denied') {
    return Notification.permission;
  }
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

export function initMessageNotificationListeners(): () => void {
  if (typeof window === 'undefined') return () => {};
  getWhistleAudio();
  const unlock = () => unlockAudioOnce();
  window.addEventListener('pointerdown', unlock, { once: true, passive: true });
  window.addEventListener('keydown', unlock, { once: true });
  // לחיצה על Web Push (הודעת מערכת מהשירות-וורקר, כשהטאב היה סגור) — פתיחת הצ'אט הרלוונטי.
  const onSwMessage = (event: MessageEvent) => {
    if (event?.data?.type === 'open-messages') {
      dispatchOpenMessagesDialog(event.data.contactId || undefined);
    }
  };
  navigator.serviceWorker?.addEventListener?.('message', onSwMessage);
  return () => {
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
    navigator.serviceWorker?.removeEventListener?.('message', onSwMessage);
  };
}

export function setMessagesDialogOpenState(open: boolean): void {
  messagesDialogOpen = open;
  if (!open) activeChatContactId = null;
}

export function setActiveMessageChatContactId(contactId: string | null): void {
  activeChatContactId = contactId ? String(contactId).trim() : null;
}

export function consumePendingOpenMessageContactId(): string | null {
  const id = pendingOpenContactId;
  pendingOpenContactId = null;
  return id;
}

export function dispatchOpenMessagesDialog(contactId?: string): void {
  if (contactId) pendingOpenContactId = String(contactId).trim();
  window.dispatchEvent(
    new CustomEvent(OPEN_MESSAGES_EVENT, { detail: { contactId: contactId || null } }),
  );
}

export function subscribeOpenMessagesDialog(handler: (contactId: string | null) => void): () => void {
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<{ contactId?: string | null }>).detail;
    const contactId = detail?.contactId ? String(detail.contactId).trim() : consumePendingOpenMessageContactId();
    handler(contactId || null);
  };
  window.addEventListener(OPEN_MESSAGES_EVENT, listener);
  return () => window.removeEventListener(OPEN_MESSAGES_EVENT, listener);
}

export function notifyIncomingMessage(message: Message, currentUserId: string): void {
  const uid = String(currentUserId || '').trim();
  const toUserId = String(message.toUserId || '').trim();
  const fromUserId = String(message.fromUserId || '').trim();
  const conversationId = String(message.conversationId || '').trim();
  const isGroup =
    message.conversationType !== 'direct' &&
    (message.conversationType === 'group' || toUserId === GROUP_MESSAGE_TO_USER_ID);
  if (!uid) return;
  if (isGroup) {
    if (!fromUserId || fromUserId === uid) return;
  } else if (toUserId !== uid || fromUserId === uid) {
    return;
  }
  if (message.isRead) return;
  if (!rememberNotified(String(message.id || ''))) return;

  const senderName = String(message.fromUserName || 'משתמש').trim() || 'משתמש';
  const groupName = String(message.conversationTitle || message.toUserName || 'קבוצה').trim();
  const subject = String(message.subject || '').trim();
  const body = trimPreview(message.content || '');
  const title = isGroup
    ? subject
      ? `${groupName} · ${senderName}: ${subject}`
      : `${groupName} · ${senderName}`
    : subject
      ? `${senderName}: ${subject}`
      : senderName;

  const suppressUi = shouldSuppressForOpenChat(fromUserId, isGroup ? conversationId : undefined);
  const pageHidden = typeof document !== 'undefined' && document.hidden;

  if (!suppressUi) {
    playNotificationSound();
    vibrateMobile();
  }

  const openKey = isGroup && conversationId ? `group:${conversationId}` : fromUserId;
  const shouldShowSystemNotification =
    pageHidden || !messagesDialogOpen || activeChatContactId !== openKey;
  if (!shouldShowSystemNotification) return;
  if (typeof window === 'undefined' || !('Notification' in window)) return;
  if (Notification.permission !== 'granted') return;

  try {
    const notification = new Notification(title, {
      body,
      icon: `${window.location.origin}/logo192.png`,
      badge: `${window.location.origin}/logo192.png`,
      tag: `msg-${message.id}`,
      dir: 'rtl',
      lang: 'he',
      renotify: true,
    });
    notification.onclick = () => {
      window.focus();
      dispatchOpenMessagesDialog(openKey);
      notification.close();
    };
  } catch {
    // ignore
  }
}
